/**
 * Sidebar — the dashboard navigation rail.
 *
 * Visual model follows the 21st.dev "SessionNavBar" reference:
 *   • Desktop: a fixed rail that expands on hover (framer-motion width
 *     animation) with staggered label fade-in.
 *   • Mobile: a slide-in drawer toggled from the dashboard header.
 *
 * Menu entries come from the role-filtered `NAV_SECTIONS` config.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { Icons } from '@/src/modules/components/Icons';
import { BrandLogo } from '@/src/modules/components/BrandLogo';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { Button } from '@/src/modules/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/src/modules/components/ui/avatar';
import { avatarSrc } from '@/src/infrastructure/lib/avatar';
import { ScrollArea } from '@/src/modules/components/ui/scroll-area';
import { Separator } from '@/src/modules/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/src/modules/components/ui/dropdown-menu';
import { NAV_SECTIONS, filterNavByRole, filterNavByPermissions } from '@/src/infrastructure/config/navigation';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';
import { useSidebar } from './SidebarContext';
import { SidebarItem, labelVariants } from './SidebarItem';

const COLLAPSED_WIDTH = '3.05rem';
const EXPANDED_WIDTH = '15rem';

/** Rail width animation. */
const railVariants = {
  open: { width: EXPANDED_WIDTH },
  closed: { width: COLLAPSED_WIDTH },
};

/** Staggers label entrance across all rows when the rail opens. */
const staggerVariants = {
  open: { transition: { staggerChildren: 0.03, delayChildren: 0.02 } },
  closed: {},
};

const railTransition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.2,
  staggerChildren: 0.1,
} as const;

/** Shared interior of both the desktop rail and the mobile drawer. */
function SidebarBody({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.permissions);

  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: filterNavByPermissions(
          filterNavByRole(section.items, user?.role ?? ''),
          permissions,
        ),
      })).filter((section) => section.items.length > 0),
    [user?.role, permissions],
  );

  const displayName = user?.displayName ?? user?.email ?? 'User';
  const email = user?.email ?? '';
  const initial = displayName.charAt(0).toUpperCase() || '?';
  const planLabel = user?.plan
    ? user.plan.charAt(0) + user.plan.slice(1).toLowerCase() + ' Plan'
    : 'Free Plan';
  // `updatedAt` is the cache-bust token: it ticks whenever the user row
  // mutates, so the browser/CDN drop their stale copy after a profile save.
  const avatarUrl = avatarSrc(user?.avatar, user?.updatedAt);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <motion.ul variants={staggerVariants} className="flex h-full flex-col">
      {/* Brand logo header */}
      <div className={cn(
        'flex w-full shrink-0 items-center border-b border-border px-3',
        collapsed ? 'h-16 justify-center py-2' : 'py-4'
      )}>
        <div className={cn(
          'flex items-center justify-center shrink-0 rounded-md',
          collapsed ? 'w-9 h-9' : 'w-full px-4 py-2'
        )}>
          <BrandLogo
            variant={collapsed ? "icon" : "full"}
            size={collapsed ? "sm" : "lg"}
            href="/dashboard"
            className={cn(
              collapsed ? 'h-9 w-9' : 'h-14 w-auto max-w-[140px]'
            )}
          />
        </div>
      </div>

      {/* Navigation */}
      <ScrollArea className="grow p-2">
        <div className="flex w-full flex-col gap-1">
          {sections.map((section, i) => (
            <React.Fragment key={section.title ?? `section-${i}`}>
              {i > 0 && <Separator className="my-1" />}
              {section.items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </ScrollArea>
      {/* Account */}
      <div className="flex flex-col gap-1 border-t border-border p-2">
        {onNavigate ? (
          /* On mobile: render account details and a direct, touch-accessible sign-out button */
          <div className="flex flex-col gap-2 p-2 w-full">
            <div className="flex flex-row items-center gap-2">
              <Avatar className="size-7">
                {avatarUrl && (
                  <AvatarImage src={avatarUrl} alt={displayName} />
                )}
                <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col text-left overflow-hidden">
                <span className="text-sm font-medium text-on-surface truncate">
                  {displayName}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {email || planLabel}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 text-status-failed hover:text-status-failed border-status-failed/20 hover:bg-status-failed/5 h-11 text-sm font-medium rounded-xl mt-1"
            >
              <Icons.LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        ) : (
          /* On desktop: keep original dropdown menu */
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger className="w-full">
              <div className="flex h-8 w-full flex-row items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground transition hover:bg-muted hover:text-primary">
                <Avatar className="size-6">
                  {avatarUrl && (
                    <AvatarImage src={avatarUrl} alt={displayName} />
                  )}
                  <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                    {initial}
                  </AvatarFallback>
                </Avatar>
                <motion.li
                  variants={labelVariants}
                  className="flex w-full items-center gap-2"
                >
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium">Account</span>
                      <Icons.ChevronsUpDown className="ml-auto h-4 w-4 text-muted-foreground/60" />
                    </>
                  )}
                </motion.li>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent sideOffset={5} className="w-60">
              <div className="flex flex-row items-center gap-2 p-2">
                <Avatar className="size-7">
                  {avatarUrl && (
                    <AvatarImage src={avatarUrl} alt={displayName} />
                  )}
                  <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                    {initial}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col text-left">
                  <span className="text-sm font-medium text-on-surface">
                    {displayName}
                  </span>
                  <span className="line-clamp-1 text-xs text-muted-foreground">
                    {email || planLabel}
                  </span>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center gap-2 text-status-failed focus:text-status-failed"
              >
                <Icons.LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
    </motion.ul>
  );
}

export function Sidebar() {
  const { mobileOpen, closeMobile } = useSidebar();
  // Desktop rail starts collapsed and expands on hover (reference behaviour).
  const [collapsed, setCollapsed] = useState(true);

  // While the mobile drawer is open: close on Escape and lock body scroll.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen, closeMobile]);

  return (
    <>
      {/* Desktop rail — in-flow, expands on hover and pushes page content. */}
      <motion.div
        id="sidebar"
        aria-label="Sidebar"
        className="z-40 hidden h-full shrink-0 border-r border-border bg-surface-lowest md:block overflow-visible"
        initial="closed"
        animate={collapsed ? 'closed' : 'open'}
        variants={railVariants}
        transition={railTransition}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
      >
        <div className="flex h-full flex-col text-muted-foreground">
          <SidebarBody collapsed={collapsed} />
        </div>
      </motion.div>

      {/* Mobile drawer */}
      <div className={cn('fixed inset-0 z-[100] md:hidden', !mobileOpen && 'pointer-events-none')}>
        <div
          onClick={closeMobile}
          aria-hidden
          className={cn(
            'absolute inset-0 bg-on-surface/40 backdrop-blur-sm transition-opacity duration-300',
            mobileOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className={cn(
            'absolute left-0 top-0 flex h-full w-64 flex-col border-r border-border bg-surface-lowest shadow-2xl',
            'transition-transform duration-300 ease-in-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <SidebarBody collapsed={false} onNavigate={closeMobile} />
        </div>
      </div>
    </>
  );
}
